import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useAuth } from "../../../contexts/AuthContext";

const OnboardingComplete = () => {
  const { user, userProfile } = useAuth();
  const [showCelebration, setShowCelebration] = useState(true);

  // Quick stats (you can integrate with real data)
  const [quickStats, setQuickStats] = useState({
    apiKeys: 0,
    webhooks: 0,
    apiCalls: 0,
    uptime: "99.9%",
  });

  const quickActions = [
    {
      title: "API Documentation",
      description: "Explore our comprehensive API docs",
      icon: "BookOpen",
      action: () => window.open("/api-documentation", "_blank"),
      variant: "default",
    },
    {
      title: "View Analytics",
      description: "Check your usage and performance",
      icon: "BarChart3",
      action: () => {
        // Navigate to analytics tab - you can implement this
        console.log("Navigate to analytics");
      },
      variant: "outline",
    },
    {
      title: "Contact Support",
      description: "Get help from our expert team",
      icon: "MessageSquare",
      action: () => {
        // Open support channel
        console.log("Open support");
      },
      variant: "outline",
    },
    {
      title: "Community Forum",
      description: "Join our developer community",
      icon: "Users",
      action: () => window.open("https://community.messaginghub.com", "_blank"),
      variant: "ghost",
    },
  ];

  const resources = [
    {
      title: "Best Practices Guide",
      description: "Learn optimization techniques",
      icon: "Lightbulb",
      badge: "Popular",
    },
    {
      title: "SDK Examples",
      description: "Ready-to-use code samples",
      icon: "Code",
      badge: "New",
    },
    {
      title: "Webhook Recipes",
      description: "Common webhook implementations",
      icon: "Zap",
      badge: null,
    },
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Celebration Header */}
      {showCelebration && (
        <div className="mb-6 p-4 bg-gradient-to-r from-accent/10 to-secondary/10 rounded-lg border border-accent/20 relative">
          <button
            onClick={() => setShowCelebration(false)}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-accent/20 rounded-full">
              <Icon name="Trophy" size={24} className="text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Welcome to MessagingHub Pro! 🎉
              </h2>
              <p className="text-sm text-muted-foreground">
                You've completed the onboarding process. Your integration is
                ready for production!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Back Header */}
      {!showCelebration && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-1">
            Welcome back,{" "}
            {userProfile?.full_name ||
              user?.email?.split("@")[0] ||
              "Developer"}
            !
          </h2>
          <p className="text-sm text-muted-foreground">
            Your integration is running smoothly. Here's what you can do next.
          </p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <Icon name="Key" size={16} className="text-muted-foreground" />
            <span className="text-lg font-semibold text-foreground">
              {quickStats.apiKeys}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">API Keys</p>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <Icon name="Zap" size={16} className="text-muted-foreground" />
            <span className="text-lg font-semibold text-foreground">
              {quickStats.webhooks}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Webhooks</p>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <Icon name="Activity" size={16} className="text-muted-foreground" />
            <span className="text-lg font-semibold text-foreground">
              {quickStats.apiCalls}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">API Calls</p>
        </div>

        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <Icon name="TrendingUp" size={16} className="text-accent" />
            <span className="text-lg font-semibold text-accent">
              {quickStats.uptime}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Uptime</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-foreground mb-3">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {quickActions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant}
              onClick={action.action}
              className="justify-start h-auto p-3 text-left"
            >
              <div className="flex items-center space-x-3">
                <Icon name={action.icon} size={18} />
                <div>
                  <div className="text-sm font-medium">{action.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {action.description}
                  </div>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </div>

      {/* Helpful Resources */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-foreground">
            Helpful Resources
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open("/api-documentation", "_blank")}
            className="text-xs"
          >
            View All
            <Icon name="ExternalLink" size={12} className="ml-1" />
          </Button>
        </div>

        <div className="space-y-3">
          {resources.map((resource, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border hover:bg-muted/40 transition-colors cursor-pointer"
              onClick={() => window.open("/api-documentation", "_blank")}
            >
              <div className="flex items-center space-x-3">
                <Icon
                  name={resource.icon}
                  size={16}
                  className="text-muted-foreground"
                />
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {resource.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {resource.description}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {resource.badge && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
                    {resource.badge}
                  </span>
                )}
                <Icon
                  name="ChevronRight"
                  size={14}
                  className="text-muted-foreground"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">
              Need help? Our support team is here to assist you.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                window.open("https://status.messaginghub.com", "_blank")
              }
              className="text-xs"
            >
              System Status
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Open support channel
                console.log("Open support");
              }}
              className="text-xs"
            >
              Get Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingComplete;
